import React from 'react';
import DOMPurify from 'dompurify';
import { cleanWavyUnderlines } from '@/utils/cleanWavyUnderlines';

interface RichTextDisplayProps {
  content: string;
  className?: string;
  fallbackText?: string;
}

const RichTextDisplay: React.FC<RichTextDisplayProps> = ({ 
  content, 
  className = "prose prose-sm max-w-none",
  fallbackText = "No description provided."
}) => {
  // Handle empty or null content
  if (!content || content.trim() === '') {
    return (
      <div className={`${className} text-muted-foreground italic`}>
        {fallbackText}
      </div>
    );
  }

  // Remove AI wavy underline styling before sanitization
  const cleanedContent = cleanWavyUnderlines(content);

  // Sanitize the HTML content
  const sanitizedContent = DOMPurify.sanitize(cleanedContent, {
    ALLOWED_TAGS: [
      'p', 'br', 'strong', 'em', 'u', 'ul', 'ol', 'li', 'a', 'span', 'div',
      'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'blockquote', 'code', 'pre',
      'table', 'thead', 'tbody', 'tr', 'th', 'td', 'colgroup', 'col'
    ],
    ALLOWED_ATTR: ['href', 'target', 'rel', 'style', 'class'],
    ALLOWED_URI_REGEXP: /^(?:(?:(?:f|ht)tps?|mailto|tel|callto|cid|xmpp):|[^a-z]|[a-z+.\-]+(?:[^a-z+.\-:]|$))/i
  });

  return (
    <div 
      className={`${className} [&_table]:w-full [&_table]:border-collapse [&_table]:my-4 [&_th]:border [&_th]:border-gray-300 [&_th]:bg-gray-50 [&_th]:px-4 [&_th]:py-2 [&_th]:text-left [&_th]:font-semibold [&_th]:text-sm [&_td]:border [&_td]:border-gray-300 [&_td]:px-4 [&_td]:py-2 [&_td]:text-sm`}
      dangerouslySetInnerHTML={{ __html: sanitizedContent }}
    />
  );
};

export { RichTextDisplay }; 